<template>
  <div class="knowledge-management">
    <div class="knowledge-header">
      <div class="header-left">
        <h2 class="knowledge-title">Company Knowledge</h2>
        <p class="knowledge-subtitle">
          Manage knowledge blocks that are injected into AI assistant system prompts. Assign blocks to specific assistants to customize their knowledge.
        </p>
      </div>
      <button class="close-btn" @click="$emit('close')" title="Close">
        <span class="close-icon">&times;</span>
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="knowledge-loading">
      <div class="spinner"></div>
      <span>Loading knowledge blocks...</span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="knowledge-error">
      <p>{{ error }}</p>
      <button class="retry-btn" @click="loadBlocks">Retry</button>
    </div>

    <!-- Main Content -->
    <div v-else>
      <!-- Create New Block Button -->
      <div class="action-bar">
        <button class="create-btn" @click="openCreateForm">
          <span class="plus-icon">+</span> New Knowledge Block
        </button>
      </div>

      <!-- Create/Edit Form Modal -->
      <div v-if="showForm" class="form-overlay" @click.self="cancelForm">
        <div class="form-modal">
          <div class="form-header">
            <h3>{{ editingBlock ? 'Edit Knowledge Block' : 'Create Knowledge Block' }}</h3>
            <button class="close-btn" @click="cancelForm" title="Close">
              <span class="close-icon">&times;</span>
            </button>
          </div>
          <div class="form-body">
            <div class="form-field">
              <label for="block-title">Title</label>
              <input
                id="block-title"
                v-model="formData.title"
                type="text"
                placeholder="Enter block title..."
                class="form-input"
                maxlength="255"
              />
            </div>
            <div class="form-field">
              <label for="block-content">Content</label>
              <textarea
                id="block-content"
                v-model="formData.content"
                placeholder="Enter knowledge content..."
                class="form-textarea"
                rows="8"
              ></textarea>
            </div>
            <div class="form-field checkbox-field">
              <label>
                <input type="checkbox" v-model="formData.is_active" />
                Active
              </label>
              <span class="checkbox-hint">Inactive blocks are not injected into prompts</span>
            </div>
            <div v-if="formError" class="form-error">{{ formError }}</div>
          </div>
          <div class="form-footer">
            <button class="cancel-btn" @click="cancelForm">Cancel</button>
            <button class="save-btn" @click="saveBlock" :disabled="saving">
              <span v-if="saving" class="btn-spinner"></span>
              {{ saving ? 'Saving...' : (editingBlock ? 'Update' : 'Create') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Assignments Modal -->
      <div v-if="showAssignments" class="form-overlay" @click.self="closeAssignments">
        <div class="form-modal assignments-modal">
          <div class="form-header">
            <h3>Manage Assignments</h3>
            <p class="assignment-subtitle">{{ assigningBlock ? assigningBlock.title : '' }}</p>
            <button class="close-btn" @click="closeAssignments" title="Close">
              <span class="close-icon">&times;</span>
            </button>
          </div>
          <div class="form-body">
            <!-- Current Assignments -->
            <div class="current-assignments">
              <h4>Assigned Assistants</h4>
              <div v-if="currentAssignments.length === 0" class="no-assignments">
                No assistants assigned yet
              </div>
              <div v-else class="assignment-list">
                <div v-for="assignment in currentAssignments" :key="assignment.id" class="assignment-item">
                  <span class="assistant-name">{{ assignment.assistant_name }}</span>
                  <button
                    class="remove-assignment-btn"
                    @click="removeAssignment(assignment)"
                    :disabled="removingAssignmentId === assignment.id"
                    title="Remove assignment"
                  >
                    <span v-if="removingAssignmentId === assignment.id" class="btn-spinner small"></span>
                    <span v-else>&times;</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Add Assignment -->
            <div class="add-assignment">
              <h4>Add Assistant</h4>
              <div class="add-assignment-row">
                <select v-model="selectedAssistantId" class="assistant-select">
                  <option value="">Select an assistant...</option>
                  <option
                    v-for="assistant in availableAssistants"
                    :key="assistant.id"
                    :value="assistant.id"
                  >
                    {{ assistant.name }}
                  </option>
                </select>
                <button
                  class="assign-btn"
                  @click="addAssignment"
                  :disabled="!selectedAssistantId || assigning"
                >
                  <span v-if="assigning" class="btn-spinner small"></span>
                  <span v-else>Assign</span>
                </button>
              </div>
              <div v-if="assignError" class="form-error">{{ assignError }}</div>
            </div>
          </div>
          <div class="form-footer">
            <button class="cancel-btn" @click="closeAssignments">Done</button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="blocks.length === 0" class="knowledge-empty">
        <div class="empty-icon">📚</div>
        <h3>No knowledge blocks yet</h3>
        <p>Create knowledge blocks to give AI assistants specialized company knowledge. Blocks can be assigned to specific assistants.</p>
      </div>

      <!-- Knowledge Block List -->
      <div v-else class="knowledge-list">
        <div class="block-count">{{ blocks.length }} {{ blocks.length === 1 ? 'block' : 'blocks' }}</div>
        <div
          v-for="block in blocks"
          :key="block.id"
          class="knowledge-item"
          :class="{ inactive: !block.is_active }"
        >
          <div class="block-header-row">
            <div class="block-title-area">
              <h4 class="block-title">{{ block.title }}</h4>
              <span v-if="!block.is_active" class="inactive-badge">Inactive</span>
            </div>
            <div class="block-actions">
              <button class="action-btn" @click="openAssignments(block)" title="Manage assistant assignments">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </button>
              <button class="action-btn" @click="openEditForm(block)" title="Edit block">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </button>
              <button class="action-btn delete" @click="confirmDelete(block)" title="Delete block">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </div>
          </div>
          <p class="block-content-preview">{{ truncateContent(block.content) }}</p>
          <div class="block-meta">
            <span class="meta-item">Created {{ formatDate(block.created_at) }}</span>
            <span v-if="block.created_by_username" class="meta-item">by {{ block.created_by_username }}</span>
            <span class="meta-item">Updated {{ formatDate(block.updated_at) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="deletingBlock" class="form-overlay" @click.self="cancelDelete">
      <div class="form-modal confirm-modal">
        <div class="form-header">
          <h3>Delete Knowledge Block</h3>
        </div>
        <div class="form-body">
          <p>Are you sure you want to delete "<strong>{{ deletingBlock.title }}</strong>"?</p>
          <p class="delete-warning">This will also remove all assistant assignments for this block. This action cannot be undone.</p>
        </div>
        <div class="form-footer">
          <button class="cancel-btn" @click="cancelDelete">Cancel</button>
          <button class="delete-confirm-btn" @click="deleteBlock" :disabled="deleting">
            <span v-if="deleting" class="btn-spinner small"></span>
            {{ deleting ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/axios-config';

export default {
  name: 'KnowledgeManagement',
  data() {
    return {
      blocks: [],
      assistants: [],
      loading: true,
      error: null,
      // Form state
      showForm: false,
      editingBlock: null,
      formData: { title: '', content: '', is_active: true },
      formError: null,
      saving: false,
      // Delete state
      deletingBlock: null,
      deleting: false,
      // Assignment state
      showAssignments: false,
      assigningBlock: null,
      currentAssignments: [],
      selectedAssistantId: '',
      assigning: false,
      assignError: null,
      removingAssignmentId: null,
      loadingAssignments: false,
    };
  },
  computed: {
    availableAssistants() {
      const assignedIds = new Set(this.currentAssignments.map(a => a.assistant_id));
      return this.assistants.filter(a => !assignedIds.has(a.id));
    },
  },
  mounted() {
    this.loadBlocks();
    this.loadAssistants();
  },
  methods: {
    async loadBlocks() {
      this.loading = true;
      this.error = null;
      try {
        const response = await api.get('/api/admin/knowledge-blocks/');
        this.blocks = response.data;
      } catch (err) {
        console.error('Failed to load knowledge blocks:', err);
        this.error = 'Failed to load knowledge blocks. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    async loadAssistants() {
      try {
        const response = await api.get('/api/assistant/');
        this.assistants = response.data;
      } catch (err) {
        console.error('Failed to load assistants:', err);
      }
    },
    // --- CRUD ---
    openCreateForm() {
      this.editingBlock = null;
      this.formData = { title: '', content: '', is_active: true };
      this.formError = null;
      this.showForm = true;
    },
    openEditForm(block) {
      this.editingBlock = block;
      this.formData = {
        title: block.title,
        content: block.content,
        is_active: block.is_active,
      };
      this.formError = null;
      this.showForm = true;
    },
    cancelForm() {
      this.showForm = false;
      this.editingBlock = null;
      this.formError = null;
    },
    async saveBlock() {
      if (!this.formData.title.trim()) {
        this.formError = 'Title is required.';
        return;
      }
      if (!this.formData.content.trim()) {
        this.formError = 'Content is required.';
        return;
      }
      this.saving = true;
      this.formError = null;
      try {
        // Ensure CSRF token
        await api.get('/api/get-csrf-token');
        const csrfToken = document.cookie
          .split('; ')
          .find(row => row.startsWith('csrftoken='))
          ?.split('=')[1];
        const headers = { 'X-CSRFToken': csrfToken };

        if (this.editingBlock) {
          // PATCH update
          await api.patch(`/api/admin/knowledge-blocks/${this.editingBlock.id}/`, this.formData, { headers });
        } else {
          // POST create
          await api.post('/api/admin/knowledge-blocks/', this.formData, { headers });
        }
        this.showForm = false;
        this.editingBlock = null;
        await this.loadBlocks();
      } catch (err) {
        console.error('Failed to save knowledge block:', err);
        this.formError = err.response?.data?.error || 'Failed to save. Please try again.';
      } finally {
        this.saving = false;
      }
    },
    // --- Delete ---
    confirmDelete(block) {
      this.deletingBlock = block;
    },
    cancelDelete() {
      this.deletingBlock = null;
    },
    async deleteBlock() {
      this.deleting = true;
      try {
        await api.get('/api/get-csrf-token');
        const csrfToken = document.cookie
          .split('; ')
          .find(row => row.startsWith('csrftoken='))
          ?.split('=')[1];
        await api.delete(`/api/admin/knowledge-blocks/${this.deletingBlock.id}/`, {
          headers: { 'X-CSRFToken': csrfToken },
        });
        this.blocks = this.blocks.filter(b => b.id !== this.deletingBlock.id);
        this.deletingBlock = null;
      } catch (err) {
        console.error('Failed to delete knowledge block:', err);
        alert('Failed to delete knowledge block. Please try again.');
      } finally {
        this.deleting = false;
      }
    },
    // --- Assignments ---
    async openAssignments(block) {
      this.assigningBlock = block;
      this.showAssignments = true;
      this.selectedAssistantId = '';
      this.assignError = null;
      this.currentAssignments = [];
      this.loadingAssignments = true;
      try {
        const response = await api.get(`/api/admin/knowledge-blocks/${block.id}/assignments/`);
        this.currentAssignments = response.data;
      } catch (err) {
        console.error('Failed to load assignments:', err);
        this.assignError = 'Failed to load assignments.';
      } finally {
        this.loadingAssignments = false;
      }
    },
    closeAssignments() {
      this.showAssignments = false;
      this.assigningBlock = null;
      this.currentAssignments = [];
      this.assignError = null;
    },
    async addAssignment() {
      if (!this.selectedAssistantId) return;
      this.assigning = true;
      this.assignError = null;
      try {
        await api.get('/api/get-csrf-token');
        const csrfToken = document.cookie
          .split('; ')
          .find(row => row.startsWith('csrftoken='))
          ?.split('=')[1];
        await api.post(
          `/api/admin/assistants/${this.selectedAssistantId}/knowledge/`,
          { knowledge_block_id: this.assigningBlock.id },
          { headers: { 'X-CSRFToken': csrfToken } }
        );
        // Reload assignments
        const response = await api.get(`/api/admin/knowledge-blocks/${this.assigningBlock.id}/assignments/`);
        this.currentAssignments = response.data;
        this.selectedAssistantId = '';
      } catch (err) {
        console.error('Failed to assign knowledge block:', err);
        this.assignError = err.response?.data?.error || 'Failed to assign. Please try again.';
      } finally {
        this.assigning = false;
      }
    },
    async removeAssignment(assignment) {
      this.removingAssignmentId = assignment.id;
      try {
        await api.get('/api/get-csrf-token');
        const csrfToken = document.cookie
          .split('; ')
          .find(row => row.startsWith('csrftoken='))
          ?.split('=')[1];
        await api.delete(
          `/api/admin/assistants/${assignment.assistant_id}/knowledge/${this.assigningBlock.id}/`,
          { headers: { 'X-CSRFToken': csrfToken } }
        );
        this.currentAssignments = this.currentAssignments.filter(a => a.id !== assignment.id);
      } catch (err) {
        console.error('Failed to remove assignment:', err);
        this.assignError = 'Failed to remove assignment. Please try again.';
      } finally {
        this.removingAssignmentId = null;
      }
    },
    // --- Helpers ---
    truncateContent(content) {
      if (!content) return '';
      return content.length > 200 ? content.slice(0, 200) + '...' : content;
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return 'today';
      if (diffDays === 1) return 'yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      const weeks = Math.floor(diffDays / 7);
      if (diffDays < 30) return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
    },
  },
};
</script>

<style scoped>
.knowledge-management {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 32px 24px;
  box-sizing: border-box;
  height: 100%;
  overflow-y: auto;
  font-family: Inter, Helvetica, sans-serif;
}

.knowledge-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
}

.header-left {
  flex: 1;
}

.knowledge-title {
  margin: 0 0 8px 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary, #1C1C1C);
}

.knowledge-subtitle {
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-secondary, #666);
  line-height: 1.5;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background-color 0.2s;
  flex-shrink: 0;
}

.close-btn:hover {
  background-color: var(--hover-bg, #e4e4e4);
}

.close-icon {
  font-size: 1.5rem;
  color: var(--text-secondary, #666);
  line-height: 1;
}

/* Loading */
.knowledge-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  gap: 16px;
  color: var(--text-secondary, #666);
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-color, #ddd);
  border-top-color: #8ECAE6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Error */
.knowledge-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 0;
  gap: 12px;
  color: #e53e3e;
}

.retry-btn {
  padding: 8px 20px;
  border: 1px solid #e53e3e;
  border-radius: 8px;
  background: none;
  color: #e53e3e;
  cursor: pointer;
  font-size: 0.88rem;
  transition: background-color 0.2s;
}

.retry-btn:hover {
  background: #fff5f5;
}

/* Action Bar */
.action-bar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.create-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #219EBC;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: background-color 0.2s;
}

.create-btn:hover {
  background: #1a7d96;
}

.plus-icon {
  font-size: 1.1rem;
  font-weight: 700;
}

/* Empty State */
.knowledge-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 16px;
}

.knowledge-empty h3 {
  margin: 0 0 12px 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary, #1C1C1C);
}

.knowledge-empty p {
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-secondary, #666);
  line-height: 1.6;
  max-width: 450px;
}

/* Knowledge List */
.block-count {
  font-size: 0.82rem;
  color: var(--text-secondary, #666);
  margin-bottom: 12px;
  font-weight: 500;
}

.knowledge-list {
  display: flex;
  flex-direction: column;
}

.knowledge-item {
  display: flex;
  flex-direction: column;
  padding: 16px;
  border: 1px solid var(--border-color, #e8e8e8);
  border-radius: 10px;
  margin-bottom: 10px;
  background: var(--bg-secondary, #fafafa);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.knowledge-item:hover {
  border-color: var(--border-hover, #d0d0d0);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.knowledge-item.inactive {
  opacity: 0.6;
}

.block-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.block-title-area {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.block-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary, #1C1C1C);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inactive-badge {
  display: inline-block;
  padding: 2px 8px;
  font-size: 0.72rem;
  font-weight: 500;
  color: #b7791f;
  background: #fefcbf;
  border-radius: 12px;
  white-space: nowrap;
}

.block-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.action-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  color: var(--text-secondary, #666);
  opacity: 0.5;
  transition: opacity 0.2s, background-color 0.2s, color 0.2s;
}

.action-btn:hover {
  opacity: 1;
  background-color: var(--hover-bg, #eee);
}

.action-btn.delete:hover {
  color: #e53e3e;
  background-color: #fff5f5;
}

.block-content-preview {
  margin: 0 0 8px 0;
  font-size: 0.88rem;
  line-height: 1.5;
  color: var(--text-secondary, #555);
  white-space: pre-wrap;
  word-break: break-word;
}

.block-meta {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.meta-item {
  font-size: 0.76rem;
  color: var(--text-tertiary, #999);
}

/* Form Modal */
.form-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.form-modal {
  background: var(--bg-primary, white);
  border-radius: 12px;
  width: 90%;
  max-width: 560px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.assignments-modal {
  max-width: 480px;
}

.confirm-modal {
  max-width: 420px;
}

.form-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 12px;
  flex-wrap: wrap;
}

.form-header h3 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--text-primary, #1C1C1C);
}

.assignment-subtitle {
  width: 100%;
  margin: 4px 0 0 0;
  font-size: 0.85rem;
  color: var(--text-secondary, #666);
}

.form-body {
  padding: 12px 24px;
  overflow-y: auto;
  flex: 1;
}

.form-field {
  margin-bottom: 16px;
}

.form-field label {
  display: block;
  margin-bottom: 6px;
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--text-primary, #1C1C1C);
}

.form-input, .form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border-color, #ddd);
  border-radius: 8px;
  font-size: 0.9rem;
  font-family: inherit;
  color: var(--text-primary, #1C1C1C);
  background: var(--bg-primary, white);
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.form-input:focus, .form-textarea:focus {
  outline: none;
  border-color: #219EBC;
  box-shadow: 0 0 0 3px rgba(33, 158, 188, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 120px;
}

.checkbox-field label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-field input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #219EBC;
}

.checkbox-hint {
  display: block;
  margin-top: 4px;
  margin-left: 24px;
  font-size: 0.78rem;
  color: var(--text-tertiary, #999);
}

.form-error {
  color: #e53e3e;
  font-size: 0.85rem;
  margin-top: 8px;
}

.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 24px 20px;
}

.cancel-btn {
  padding: 8px 16px;
  border: 1px solid var(--border-color, #ddd);
  border-radius: 8px;
  background: none;
  color: var(--text-primary, #333);
  cursor: pointer;
  font-size: 0.88rem;
  transition: background-color 0.2s;
}

.cancel-btn:hover {
  background-color: var(--hover-bg, #f5f5f5);
}

.save-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  background: #219EBC;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 500;
  transition: background-color 0.2s;
}

.save-btn:hover:not(:disabled) {
  background: #1a7d96;
}

.save-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.delete-confirm-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  background: #e53e3e;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 500;
  transition: background-color 0.2s;
}

.delete-confirm-btn:hover:not(:disabled) {
  background: #c53030;
}

.delete-confirm-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.delete-warning {
  color: var(--text-secondary, #666);
  font-size: 0.88rem;
  margin-top: 8px;
}

.btn-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.btn-spinner.small {
  width: 12px;
  height: 12px;
  border-width: 1.5px;
}

/* Assignments */
.current-assignments {
  margin-bottom: 20px;
}

.current-assignments h4, .add-assignment h4 {
  margin: 0 0 10px 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary, #1C1C1C);
}

.no-assignments {
  padding: 12px;
  text-align: center;
  color: var(--text-secondary, #999);
  font-size: 0.88rem;
  background: var(--bg-secondary, #f9f9f9);
  border-radius: 8px;
}

.assignment-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.assignment-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: var(--bg-secondary, #f5f5f5);
  border-radius: 8px;
  border: 1px solid var(--border-color, #e8e8e8);
}

.assistant-name {
  font-size: 0.88rem;
  color: var(--text-primary, #1C1C1C);
  font-weight: 500;
}

.remove-assignment-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  color: var(--text-secondary, #666);
  font-size: 1.1rem;
  line-height: 1;
  transition: color 0.2s, background-color 0.2s;
}

.remove-assignment-btn:hover {
  color: #e53e3e;
  background-color: #fff5f5;
}

.remove-assignment-btn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.add-assignment-row {
  display: flex;
  gap: 8px;
}

.assistant-select {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid var(--border-color, #ddd);
  border-radius: 8px;
  font-size: 0.88rem;
  font-family: inherit;
  color: var(--text-primary, #1C1C1C);
  background: var(--bg-primary, white);
}

.assistant-select:focus {
  outline: none;
  border-color: #219EBC;
}

.assign-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  background: #219EBC;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 500;
  transition: background-color 0.2s;
  white-space: nowrap;
}

.assign-btn:hover:not(:disabled) {
  background: #1a7d96;
}

.assign-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
